import React from 'react';
import { DatabaseState, AppConfig, Gallery, ContactLead, ClientDelivery, GuestComment } from './types';
import { defaultData } from './data/defaultData';
import { sanitizeConfig } from './utils/sanitizeConfig';
import { db, doc, setDoc, deleteDoc, onSnapshot, collection } from './lib/firebase';
import { Header } from './components/Header';
import { Hero } from './components/Hero';
import { GalleryGrid } from './components/GalleryGrid';
import { DuoProfile } from './components/DuoProfile';
import { Showreel } from './components/Showreel';
import { FeaturedMasterpiece } from './components/FeaturedMasterpiece';
import { AiSeoDashboard } from './components/AiSeoDashboard';
import { ContactForm } from './components/ContactForm';
import { ServiceAreas } from './components/ServiceAreas';
import { Faq } from './components/Faq';
import { TestimonialsTicker } from './components/TestimonialsTicker';
import { VisualEditorBar } from './components/VisualEditorBar';
import { ClientDeliveryView } from './components/ClientDeliveryView';
import { ClientAreaGlobal } from './components/ClientAreaGlobal';
import { Sparkles, Camera, Film, ArrowUp, Heart, Check, MapPin } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';

const AdminPanel = React.lazy(() => import('./components/AdminPanel').then(m => ({ default: m.AdminPanel })));

export const themeStyles: Record<string, string> = {
  'cosmic-charcoal': `
    :root {
      --theme-bg: #09090b;
      --theme-card: #121214;
      --theme-card-hover: #1c1c1f;
      --theme-text: #ffffff;
      --theme-secondary: #a1a1aa;
      --theme-accent: #fbbf24;
      --theme-accent-hover: #f59e0b;
      --theme-border: rgba(255, 255, 255, 0.06);
      --theme-border-hover: rgba(255, 255, 255, 0.12);
    }
  `,
  'sherlock-wedding': `
    @import url('https://fonts.googleapis.com/css2?family=Cinzel:wght@400;500;600;700&family=Playfair+Display:ital,wght@0,400;0,500;0,600;0,700;1,400;1,500&family=Pinyon+Script&display=swap');
    :root {
      --theme-bg: #130f0c;
      --theme-card: #1c1510;
      --theme-card-hover: #261d16;
      --theme-text: #f3e6d8;
      --theme-secondary: #c2a78f;
      --theme-accent: #dfa95c;
      --theme-accent-hover: #f3c283;
      --theme-border: rgba(223, 169, 92, 0.14);
      --theme-border-hover: rgba(223, 169, 92, 0.32);
      
      --font-serif: "Cinzel", Georgia, serif;
      --font-sans: "Playfair Display", Georgia, serif;
      --font-cursive: "Pinyon Script", cursive;
    }
    
    /* Elegant Vintage Sherlock Holmes Atmospheric Touches */
    body {
      background-image: radial-gradient(circle at 50% 0%, rgba(223, 169, 92, 0.09) 0%, transparent 65%);
    }

    /* Style vintage paper borders and classic double rules */
    .sherlock-badge {
      border: 1px solid rgba(223, 169, 92, 0.4);
      background: rgba(223, 169, 92, 0.05);
      font-family: var(--font-serif);
      letter-spacing: 0.15em;
    }

    /* Stylized vintage scrollbars */
    ::-webkit-scrollbar-track {
      background: #130f0c;
    }
    ::-webkit-scrollbar-thumb {
      background: rgba(223, 169, 92, 0.2);
      border: 1px solid #130f0c;
    }
    ::-webkit-scrollbar-thumb:hover {
      background: #dfa95c;
    }
  `
};

export default function App() {
  const [dbState, setDbState] = React.useState<DatabaseState>(defaultData);
  const [isAdminOpen, setIsAdminOpen] = React.useState(false);
  const [isClientAreaOpen, setIsClientAreaOpen] = React.useState<boolean>(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      return params.get('client') === 'true' || params.get('clientArea') === 'true';
    } catch {
      return false;
    }
  });
  const [isAdminAuthenticated, setIsAdminAuthenticated] = React.useState<boolean>(() => {
    try {
      return sessionStorage.getItem('admin_authenticated') === 'true';
    } catch {
      return false;
    }
  });
  const [isVisualEditActive, setIsVisualEditActive] = React.useState(true);
  const [showScrollTop, setShowScrollTop] = React.useState(false);
  const [toast, setToast] = React.useState<string | null>(null);

  const handleAuthenticateAdmin = () => {
    setIsAdminAuthenticated(true);
    try {
      sessionStorage.setItem('admin_authenticated', 'true');
    } catch (e) {
      console.error('Session storage error:', e);
    }
  };

  const handleLogoutAdmin = () => {
    setIsAdminAuthenticated(false);
    setIsAdminOpen(false);
    try {
      sessionStorage.removeItem('admin_authenticated');
    } catch (e) {
      console.error('Session storage error:', e);
    }
    showToast('Session administrateur fermée');
  };

  // Load from database / localStorage / Firebase real-time
  React.useEffect(() => {
    // 1. Try local storage first for instant render
    const cached = localStorage.getItem('life_captured_db');
    let cachedDb: DatabaseState | null = null;
    if (cached) {
      try {
        cachedDb = JSON.parse(cached);
        if (cachedDb) {
          cachedDb.config = sanitizeConfig(cachedDb.config);
          setDbState(cachedDb);
        }
      } catch (e) {
        console.error('Error parsing localStorage cache', e);
      }
    }

    // 2. Fetch fresh state from express backend
    fetch('/api/db')
      .then(res => {
        if (!res.ok) throw new Error('API server down');
        return res.json();
      })
      .then((serverData: DatabaseState) => {
        const cleanServerGalleries = (serverData.galleries || []).filter(g => !['gal-1', 'gal-2', 'gal-3'].includes(g.id));
        setDbState(prev => {
          // Keep Firestore galleries if we already received them, otherwise use server galleries
          const currentGalleries = prev.galleries.length > 0 ? prev.galleries : cleanServerGalleries;
          const mergedConfig = sanitizeConfig(cachedDb?.config ? { ...serverData.config, ...cachedDb.config } : serverData.config);
          const currentLogs = (prev.downloadLogs && prev.downloadLogs.length > 0) ? prev.downloadLogs : (serverData.downloadLogs || cachedDb?.downloadLogs || []);
          const mergedState: DatabaseState = {
            ...serverData,
            galleries: currentGalleries,
            config: mergedConfig,
            downloadLogs: currentLogs,
            deliveries: (prev.deliveries && prev.deliveries.length > 0) ? prev.deliveries : (serverData.deliveries || cachedDb?.deliveries || []),
            leads: (prev.leads && prev.leads.length > 0) ? prev.leads : (serverData.leads || cachedDb?.leads || [])
          };
          localStorage.setItem('life_captured_db', JSON.stringify(mergedState));
          return mergedState;
        });
      })
      .catch(err => {
        console.warn('Backend API offline or loading locally. Graceful fallback activated.', err);
      });

    // 3. Real-Time Firebase Firestore Synchronization
    let unsubscribeGalleries: (() => void) | null = null;
    let unsubscribeLeads: (() => void) | null = null;
    let unsubscribeConfig: (() => void) | null = null;
    let unsubscribeDeliveries: (() => void) | null = null;
    let isSubscribed = false;

    const startFirestoreSync = () => {
      if (isSubscribed) return;
      isSubscribed = true;

      try {
        // Real-Time listener for site configuration (config/siteConfig)
        unsubscribeConfig = onSnapshot(doc(db, "config", "siteConfig"), (docSnap) => {
          if (docSnap.exists()) {
            const firestoreConfig = docSnap.data() as AppConfig;
            if (firestoreConfig) {
              setDbState(prev => {
                const mergedConfig = sanitizeConfig({ 
                  ...prev.config, 
                  ...firestoreConfig 
                });

                // Synchronize duoImage1 & duoImage2 with team avatar URLs if provided
                if (firestoreConfig.team && Array.isArray(firestoreConfig.team)) {
                  mergedConfig.team = firestoreConfig.team;
                } else if (mergedConfig.team) {
                  mergedConfig.team = [...mergedConfig.team];
                }

                if (firestoreConfig.duoImage1 && mergedConfig.team && mergedConfig.team[0]) {
                  mergedConfig.team[0] = { ...mergedConfig.team[0], avatarUrl: firestoreConfig.duoImage1 };
                }
                if (firestoreConfig.duoImage2 && mergedConfig.team && mergedConfig.team[1]) {
                  mergedConfig.team[1] = { ...mergedConfig.team[1], avatarUrl: firestoreConfig.duoImage2 };
                }

                const updated = { ...prev, config: mergedConfig };
                localStorage.setItem('life_captured_db', JSON.stringify(updated));
                return updated;
              });
            }
          }
        }, (error) => {
          console.warn("Firestore real-time config listener offline:", error);
        });

        unsubscribeGalleries = onSnapshot(collection(db, "galleries"), (snapshot) => {
          const firestoreGalleries: Gallery[] = [];
          snapshot.forEach((docSnap) => {
            const data = docSnap.data() as Gallery;
            if (data && data.id && !['gal-1', 'gal-2', 'gal-3'].includes(data.id)) {
              firestoreGalleries.push(data);
            }
          });
          
          // Sort galleries by order if available
          firestoreGalleries.sort((a, b) => {
            const orderA = typeof a.order === 'number' ? a.order : 999;
            const orderB = typeof b.order === 'number' ? b.order : 999;
            return orderA - orderB;
          });

          setDbState(prev => {
            const updated = { ...prev, galleries: firestoreGalleries };
            localStorage.setItem('life_captured_db', JSON.stringify(updated));
            return updated;
          });
        }, (error) => {
          console.warn("Firestore real-time galleries listener offline:", error);
        });

        unsubscribeLeads = onSnapshot(collection(db, "contact_requests"), (snapshot) => {
          const firestoreLeads: ContactLead[] = [];
          snapshot.forEach((docSnap) => {
            firestoreLeads.push(docSnap.data() as ContactLead);
          });
          firestoreLeads.sort((a, b) => new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime());
          setDbState(prev => {
            const updated = { ...prev, leads: firestoreLeads };
            localStorage.setItem('life_captured_db', JSON.stringify(updated));
            return updated;
          });
        }, (error) => {
          console.warn("Firestore real-time leads listener offline:", error);
        });

        unsubscribeDeliveries = onSnapshot(collection(db, "client_deliveries"), (snapshot) => {
          const firestoreDeliveries: ClientDelivery[] = [];
          snapshot.forEach((docSnap) => {
            firestoreDeliveries.push(docSnap.data() as ClientDelivery);
          });
          firestoreDeliveries.sort((a, b) => new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime());
          setDbState(prev => {
            const updated = { ...prev, deliveries: firestoreDeliveries };
            localStorage.setItem('life_captured_db', JSON.stringify(updated));
            return updated;
          });
        }, (error) => {
          console.warn("Firestore real-time deliveries listener offline:", error);
        });

        let unsubscribeLogs: (() => void) | null = null;
        try {
          unsubscribeLogs = onSnapshot(collection(db, "download_logs"), (snapshot) => {
            const firestoreLogs: import('./types').DownloadLog[] = [];
            snapshot.forEach((docSnap) => {
              firestoreLogs.push(docSnap.data() as import('./types').DownloadLog);
            });
            firestoreLogs.sort((a, b) => new Date(b.timestamp || 0).getTime() - new Date(a.timestamp || 0).getTime());
            setDbState(prev => {
              const updated = { ...prev, downloadLogs: firestoreLogs };
              localStorage.setItem('life_captured_db', JSON.stringify(updated));
              return updated;
            });
          }, (error) => {
            console.warn("Firestore download logs listener offline:", error);
          });
        } catch (err) {
          console.warn("Firestore download logs sync error:", err);
        }
      } catch (e) {
        console.warn("Firestore listener setup error:", e);
      }
    };

    // Immediately start real-time sync at startup
    startFirestoreSync();

    return () => {
      if (unsubscribeConfig) unsubscribeConfig();
      if (unsubscribeGalleries) unsubscribeGalleries();
      if (unsubscribeLeads) unsubscribeLeads();
      if (unsubscribeDeliveries) unsubscribeDeliveries();
    };
  }, []);

  // Monitor scroll for ScrollToTop button
  React.useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 600);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Dynamic HTML Meta Tags update (SEO & Social Sharing Preview / Open Graph)
  React.useEffect(() => {
    if (!dbState?.config?.seo) return;
    const { metaTitle, metaDescription, ogImage } = dbState.config.seo;
    const shareImg = ogImage || dbState.config.featuredImageUrl || "https://www.life-captured.fr/photos/Aurelie/8-Photos-De-Groupe-25_Comp.webp";

    if (metaTitle) {
      document.title = metaTitle;
    }

    const updateMetaTag = (selector: string, content: string) => {
      const element = document.querySelector(selector);
      if (element) {
        element.setAttribute('content', content);
      }
    };

    if (metaDescription) {
      updateMetaTag('meta[name="description"]', metaDescription);
      updateMetaTag('meta[property="og:description"]', metaDescription);
      updateMetaTag('meta[property="twitter:description"]', metaDescription);
    }

    if (shareImg) {
      updateMetaTag('meta[property="og:image"]', shareImg);
      updateMetaTag('meta[property="twitter:image"]', shareImg);
    }
  }, [dbState?.config?.seo, dbState?.config?.featuredImageUrl]);

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 3500);
  };

  // 1. Update general config
  const handleUpdateConfig = async (newConfig: Partial<AppConfig>): Promise<boolean> => {
    const currentConfig = dbState.config || defaultData.config;
    const mergedConfig = sanitizeConfig({
      ...currentConfig,
      ...newConfig,
      contact: {
        ...(currentConfig.contact || defaultData.config.contact),
        ...(newConfig.contact || {})
      },
      social: {
        ...(currentConfig.social || defaultData.config.social),
        ...(newConfig.social || {})
      },
      seo: {
        ...(currentConfig.seo || defaultData.config.seo),
        ...(newConfig.seo || {})
      }
    });

    // 1a. Write to Firebase Firestore
    try {
      await setDoc(doc(db, "config", "siteConfig"), mergedConfig, { merge: true });
    } catch (e) {
      console.warn("Firebase config write failed, fallback to local:", e);
    }

    // 1b. Write to Express server
    try {
      await fetch('/api/db/config', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(mergedConfig)
      });
    } catch (e) {
      console.error(e);
    }

    const updated = { ...dbState, config: mergedConfig };
    setDbState(updated);
    localStorage.setItem('life_captured_db', JSON.stringify(updated));
    showToast('Configuration enregistrée & synchronisée au Cloud !');
    return true;
  };

  // 2. Save or update a gallery
  const handleUpdateGallery = async (newGallery: Gallery): Promise<boolean> => {
    // 2a. Write to Firebase Firestore
    try {
      await setDoc(doc(db, "galleries", newGallery.id), newGallery, { merge: true });
    } catch (e) {
      console.warn("Firebase gallery write error, fallback active:", e);
    }

    // 2b. Write to Express backend
    try {
      await fetch('/api/db/galleries', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newGallery)
      });
    } catch (e) {
      console.error(e);
    }

    const updatedGalleries = [...dbState.galleries];
    const index = updatedGalleries.findIndex(g => g.id === newGallery.id);
    if (index >= 0) {
      updatedGalleries[index] = newGallery;
    } else {
      updatedGalleries.push(newGallery);
    }
    const updated = { ...dbState, galleries: updatedGalleries };
    setDbState(updated);
    localStorage.setItem('life_captured_db', JSON.stringify(updated));
    showToast(`Galerie "${newGallery.title}" enregistrée & synchronisée Cloud !`);
    return true;
  };

  // 2b. Reorder all galleries
  const handleReorderGalleries = async (reorderedGalleries: Gallery[]): Promise<boolean> => {
    const updatedGalleries = reorderedGalleries.map((gal, idx) => ({
      ...gal,
      order: idx
    }));

    const updated = { ...dbState, galleries: updatedGalleries };
    setDbState(updated);
    localStorage.setItem('life_captured_db', JSON.stringify(updated));

    // Persist to Cloud Firebase & Express backend asynchronously
    try {
      for (const gal of updatedGalleries) {
        await setDoc(doc(db, "galleries", gal.id), gal, { merge: true });
        fetch('/api/db/galleries', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(gal)
        }).catch(console.error);
      }
    } catch (e) {
      console.warn("Firebase reorder write error:", e);
    }

    showToast("Ordre des galeries mis à jour et synchronisé !");
    return true;
  };

  // 3b. Toggle favorite media in a gallery
  const handleToggleFavoriteMedia = async (galleryId: string, mediaId: string) => {
    const gallery = dbState.galleries.find(g => g.id === galleryId);
    if (!gallery) return;

    let isNowFavorite = false;
    const updatedMedia = gallery.media.map(m => {
      if (m.id === mediaId) {
        isNowFavorite = !m.isFavorite;
        return { ...m, isFavorite: isNowFavorite };
      }
      return m;
    });

    const updatedGallery = { ...gallery, media: updatedMedia };
    await handleUpdateGallery(updatedGallery);
    showToast(isNowFavorite ? "Photo ajoutée aux Chefs-d'œuvre ⭐" : "Photo retirée des Chefs-d'œuvre");
  };

  // 4. Delete gallery
  const handleDeleteGallery = async (id: string): Promise<boolean> => {
    // 3a. Delete from Firebase Firestore
    try {
      await deleteDoc(doc(db, "galleries", id));
    } catch (e) {
      console.warn("Firebase gallery delete error:", e);
    }

    // 3b. Delete from Express backend
    try {
      await fetch(`/api/db/galleries/${id}`, {
        method: 'DELETE'
      });
    } catch (e) {
      console.error(e);
    }

    const updatedGalleries = dbState.galleries.filter(g => g.id !== id);
    const updated = { ...dbState, galleries: updatedGalleries };
    setDbState(updated);
    localStorage.setItem('life_captured_db', JSON.stringify(updated));
    showToast('Galerie supprimée du Cloud et du site !');
    return true;
  };

  // 3b. Delete lead request
  const handleDeleteLead = async (id: string): Promise<boolean> => {
    try {
      await deleteDoc(doc(db, "contact_requests", id));
    } catch (e) {
      console.warn("Firebase lead delete error:", e);
    }

    try {
      await fetch(`/api/leads/${id}`, {
        method: 'DELETE'
      });
    } catch (e) {
      console.error(e);
    }

    const updatedLeads = (dbState.leads || []).filter(l => l.id !== id);
    const updated = { ...dbState, leads: updatedLeads };
    setDbState(updated);
    localStorage.setItem('life_captured_db', JSON.stringify(updated));
    showToast('Demande de devis supprimée !');
    return true;
  };

  // Handlers for Client Deliveries
  const handleUpdateDelivery = async (delivery: ClientDelivery): Promise<boolean> => {
    // 1. Save to Firebase Firestore
    try {
      await setDoc(doc(db, "client_deliveries", delivery.id), delivery, { merge: true });
    } catch (e) {
      console.warn("Firebase delivery write error:", e);
    }

    // 2. Save to Express server db.json
    try {
      await fetch('/api/db/deliveries', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(delivery)
      });
    } catch (e) {
      console.error("Express delivery write error:", e);
    }

    // 3. Update React local state & localStorage
    const currentDeliveries = [...(dbState.deliveries || [])];
    const index = currentDeliveries.findIndex(d => d.id === delivery.id);
    if (index >= 0) {
      currentDeliveries[index] = delivery;
    } else {
      currentDeliveries.unshift(delivery);
    }

    const updated = { ...dbState, deliveries: currentDeliveries };
    setDbState(updated);
    localStorage.setItem('life_captured_db', JSON.stringify(updated));
    showToast('Livraison client enregistrée & sécurisée Cloud !');
    return true;
  };

  const handleDeleteDelivery = async (id: string): Promise<boolean> => {
    // 1. Delete from Firebase Firestore
    try {
      await deleteDoc(doc(db, "client_deliveries", id));
    } catch (e) {
      console.warn("Firebase delivery delete error:", e);
    }

    // 2. Delete from Express server db.json
    try {
      await fetch(`/api/db/deliveries/${id}`, { method: 'DELETE' });
    } catch (e) {
      console.error("Express delivery delete error:", e);
    }

    // 3. Update React local state & localStorage
    const updatedDeliveries = (dbState.deliveries || []).filter(d => d.id !== id);
    const updated = { ...dbState, deliveries: updatedDeliveries };
    setDbState(updated);
    localStorage.setItem('life_captured_db', JSON.stringify(updated));
    showToast('Livraison supprimée !');
    return true;
  };

  const handleDeleteDownloadLog = async (id: string): Promise<boolean> => {
    try {
      await deleteDoc(doc(db, "download_logs", id));
    } catch (e) {
      console.warn("Firestore delete log error:", e);
    }
    try {
      await fetch(`/api/download-logs/${id}`, { method: 'DELETE' });
    } catch (e) {
      console.error("Express delete log error:", e);
    }
    const updatedLogs = (dbState.downloadLogs || []).filter(l => l.id !== id);
    const updated = { ...dbState, downloadLogs: updatedLogs };
    setDbState(updated);
    localStorage.setItem('life_captured_db', JSON.stringify(updated));
    return true;
  };

  const handleClearDownloadLogs = async (): Promise<boolean> => {
    try {
      await fetch('/api/download-logs', { method: 'DELETE' });
    } catch (e) {
      console.error("Express clear logs error:", e);
    }
    const updated = { ...dbState, downloadLogs: [] };
    setDbState(updated);
    localStorage.setItem('life_captured_db', JSON.stringify(updated));
    showToast('Historique des téléchargements effacé');
    return true;
  };

  const handleRefreshDownloadLogs = async (): Promise<void> => {
    try {
      const res = await fetch('/api/download-logs');
      if (res.ok) {
        const logs = await res.json();
        setDbState(prev => {
          const updated = { ...prev, downloadLogs: logs };
          localStorage.setItem('life_captured_db', JSON.stringify(updated));
          return updated;
        });
      }
    } catch (e) {
      console.warn("Refresh download logs error:", e);
    }
  };

  // 4. Reset DB to default presets
  const handleResetDb = async (): Promise<boolean> => {
    try {
      const res = await fetch('/api/db/reset', { method: 'POST' });
      if (res.ok) {
        setDbState(defaultData);
        localStorage.setItem('life_captured_db', JSON.stringify(defaultData));
        showToast('Site réinitialisé avec succès !');
        return true;
      }
    } catch (e) {
      console.error(e);
    }
    setDbState(defaultData);
    localStorage.setItem('life_captured_db', JSON.stringify(defaultData));
    showToast('Réinitialisé localement (Mode déconnecté)');
    return true;
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const activeTheme = dbState.config.theme || 'cosmic-charcoal';

  const handleAddComment = async (deliveryId: string, comment: GuestComment) => {
    const delivery = dbState.deliveries?.find(d => d.id === deliveryId);
    if (!delivery) return;

    const existingComments = delivery.comments || [];
    const updatedDelivery: ClientDelivery = {
      ...delivery,
      comments: [comment, ...existingComments]
    };

    try {
      await setDoc(doc(db, "client_deliveries", deliveryId), updatedDelivery, { merge: true });
      // Update local state as well
      setDbState(prev => {
        const updatedDeliveries = (prev.deliveries || []).map(d => d.id === deliveryId ? updatedDelivery : d);
        const newState = { ...prev, deliveries: updatedDeliveries };
        localStorage.setItem('life_captured_db', JSON.stringify(newState));
        return newState;
      });
      showToast('Mot ajouté au Livre d\'Or !');
    } catch (e) {
      console.error(e);
    }
  };

  // Check if we are viewing a specific client delivery
  const urlParams = new URLSearchParams(window.location.search);
  const deliveryId = urlParams.get('delivery');
  const isGuestShareMode = urlParams.get('mode') === 'guest';
  
  if (deliveryId) {
    const delivery = dbState.deliveries?.find(d => d.id === deliveryId);
    if (delivery) {
      return (
        <ClientDeliveryView 
          delivery={delivery} 
          config={dbState.config} 
          isShareMode={isGuestShareMode}
          onAddComment={handleAddComment}
          onClose={() => {
            window.location.href = window.location.origin + window.location.pathname;
          }}
        />
      );
    }
  }

  if (isClientAreaOpen) {
    return (
      <ClientAreaGlobal 
        deliveries={dbState.deliveries || []} 
        config={dbState.config} 
        onClose={() => {
          const url = new URL(window.location.href);
          url.searchParams.delete('client');
          url.searchParams.delete('clientArea');
          window.history.pushState({}, '', url.toString());
          setIsClientAreaOpen(false);
        }} 
        onAddComment={handleAddComment}
      />
    );
  }

  return (
    <div className="bg-theme-bg text-theme-text min-h-screen relative font-sans selection:bg-theme-accent selection:text-theme-bg transition-colors duration-500">
      <style dangerouslySetInnerHTML={{ __html: themeStyles[activeTheme] }} />
      
      {/* Visual In-Place Editor Bar (Visible when Admin session is active) */}
      <VisualEditorBar
        isAdminAuthenticated={isAdminAuthenticated}
        isVisualEditActive={isVisualEditActive}
        onToggleVisualEdit={() => setIsVisualEditActive(!isVisualEditActive)}
        onOpenAdmin={() => setIsAdminOpen(true)}
        onLogout={handleLogoutAdmin}
      />

      {/* Floating Header */}
      <Header
        onClientAreaClick={() => setIsClientAreaOpen(true)}
        studioName={dbState.config?.studioName || defaultData.config.studioName}
        logoUrl={dbState.config?.logoUrl}
        onAdminToggle={() => setIsAdminOpen(!isAdminOpen)}
        isAdminOpen={isAdminOpen}
        isAdminAuthenticated={isAdminAuthenticated}
      />

      {/* Hero Section */}
      <Hero
        studioName={dbState.config?.studioName || defaultData.config.studioName}
        tagline={dbState.config?.tagline || defaultData.config.tagline}
        description={dbState.config?.description || defaultData.config.description}
        city={dbState.config?.contact?.city || defaultData.config.contact.city}
        weddingsCount={dbState.config?.weddingsCount ?? defaultData.config.weddingsCount}
        chateauxCount={dbState.config?.chateauxCount ?? defaultData.config.chateauxCount}
        isVisualEditActive={isAdminAuthenticated && isVisualEditActive}
        onUpdateConfig={handleUpdateConfig}
        contact={dbState.config?.contact || defaultData.config.contact}
      />

      {/* Cinematic Showreel Trailer */}
      <Showreel 
        url={dbState.config?.showreelUrl || defaultData.config.showreelUrl} 
        embedCode={dbState.config?.showreelEmbedCode} 
        showreels={dbState.config?.showreels || defaultData.config.showreels}
      />

      {/* Portfolio / Bento Grid Galleries */}
      <GalleryGrid 
        galleries={dbState.galleries} 
        theme={activeTheme} 
        displayMode={dbState.config?.galleryDisplayMode || 'grid'} 
        onToggleFavoriteMedia={handleToggleFavoriteMedia}
      />

      {/* Featured Masterpiece Section */}
      <FeaturedMasterpiece 
        galleries={dbState.galleries}
        imageUrl={dbState.config?.featuredImageUrl} 
        quote={dbState.config?.masterpieceQuote || defaultData.config.masterpieceQuote}
        author={dbState.config?.masterpieceAuthor || defaultData.config.masterpieceAuthor}
        isAdmin={isAdminAuthenticated} 
        isVisualEditActive={isAdminAuthenticated && isVisualEditActive}
        onUpdateConfig={handleUpdateConfig}
        onToggleFavoriteMedia={handleToggleFavoriteMedia}
      />

      {/* Duo Profile Team Bio */}
      <DuoProfile 
        team={dbState.config?.team || defaultData.config.team} 
        isVisualEditActive={isAdminAuthenticated && isVisualEditActive}
        onUpdateConfig={handleUpdateConfig}
      />

      {/* AI SEO & GEO Generative Engine Optimization panel */}
      <AiSeoDashboard seo={dbState.config?.seo || defaultData.config.seo} config={dbState.config || defaultData.config} />

      {/* Local SEO geographic areas section */}
      <ServiceAreas />

      {/* Frequently Asked Questions */}
      <Faq 
        theme={activeTheme} 
        faqs={dbState.config?.seo?.faqs || defaultData.config.seo.faqs}
        seo={dbState.config?.seo || defaultData.config.seo}
        isVisualEditActive={isAdminAuthenticated && isVisualEditActive}
        onUpdateConfig={handleUpdateConfig}
      />

      {/* Dynamic scrolling testimonials track */}
      <TestimonialsTicker testimonials={dbState.config?.testimonials || defaultData.config.testimonials} />

      {/* Contact Form gateway */}
      <ContactForm 
        contact={dbState.config?.contact || defaultData.config.contact} 
        social={dbState.config?.social || defaultData.config.social} 
        isVisualEditActive={isAdminAuthenticated && isVisualEditActive}
        onUpdateConfig={handleUpdateConfig}
      />

      {/* Physical map element & Localization details */}
      <div className="bg-theme-card border-t border-theme-border py-12 text-center text-theme-secondary text-xs">
        <div className="max-w-4xl mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-2 text-left">
            <MapPin className="w-5 h-5 text-theme-accent flex-shrink-0" />
            <div>
              <p className="font-mono text-[9px] uppercase tracking-widest text-theme-secondary">Siège Social & Studio</p>
              <p className="text-sm text-theme-text font-medium">6 rue Jacques Beynel, 33470 Le Teich</p>
            </div>
          </div>
          <p className="font-mono text-[10px] uppercase tracking-wider">
            Photographe Aurélie & Vidéaste Yohann © {new Date().getFullYear()} LIFE-CAPTURED — love
          </p>
        </div>
      </div>

      {/* Floating Action Admin Drawer Modal */}
      {isAdminOpen && (
        <React.Suspense fallback={null}>
          <AdminPanel
            dbState={dbState}
            onUpdateConfig={handleUpdateConfig}
            onUpdateGallery={handleUpdateGallery}
            onReorderGalleries={handleReorderGalleries}
            onDeleteGallery={handleDeleteGallery}
            onDeleteLead={handleDeleteLead}
            onUpdateDelivery={handleUpdateDelivery}
            onDeleteDelivery={handleDeleteDelivery}
            onDeleteDownloadLog={handleDeleteDownloadLog}
            onClearDownloadLogs={handleClearDownloadLogs}
            onRefreshDownloadLogs={handleRefreshDownloadLogs}
            onResetDb={handleResetDb}
            onClose={() => setIsAdminOpen(false)}
            isAdminAuthenticated={isAdminAuthenticated}
            onAuthenticateAdmin={handleAuthenticateAdmin}
            onLogout={handleLogoutAdmin}
          />
        </React.Suspense>
      )}

      {/* Floating Toast Notification */}
      <AnimatePresence>
        {toast && (
          <motion.div
            initial={{ opacity: 0, y: 50, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            className="fixed bottom-6 right-6 z-50 bg-theme-accent border border-theme-accent text-theme-bg px-5 py-3 shadow-2xl font-mono text-xs uppercase font-bold flex items-center gap-2"
          >
            <Check className="w-4 h-4 text-theme-bg" />
            <span>{toast}</span>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Scroll to Top */}
      <AnimatePresence>
        {showScrollTop && (
          <motion.button
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            onClick={scrollToTop}
            className="fixed bottom-6 left-6 z-40 p-3 rounded-none bg-theme-bg/60 hover:bg-theme-accent border border-theme-border hover:border-theme-accent text-theme-secondary hover:text-theme-bg transition-all cursor-pointer shadow-lg"
            id="scroll-to-top-btn"
            aria-label="Retourner en haut de la page"
            title="Retourner en haut"
          >
            <ArrowUp className="w-5 h-5" />
          </motion.button>
        )}
      </AnimatePresence>

    </div>
  );
}
